import { useState } from "react";
import { Mail, Phone, MapPin, Linkedin, ArrowRight, Check, AlertCircle, Loader2 } from "lucide-react";
import { BRAND } from "@/content/thoratech";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Basic client-side validators (server does the real check)
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidPhone = (v) => (v.match(/\d/g) || []).length >= 7;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
    website: "", // honeypot — must stay empty
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 2) errs.name = "Please enter your name.";
    if (!form.company.trim()) errs.company = "Please enter your company.";
    if (!EMAIL_RE.test(form.email.trim())) errs.email = "Please enter a valid work email.";
    if (!isValidPhone(form.phone)) errs.phone = "Please enter a valid phone number.";
    if (!form.message.trim() || form.message.trim().length < 10)
      errs.message = "Please share a little more about your enquiry.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(`${API_BASE}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        let msg = "Something went wrong. Please try again.";
        try {
          const data = await res.json();
          if (data && data.detail) msg = typeof data.detail === "string" ? data.detail : msg;
        } catch (_) {
          /* ignore parse error */
        }
        throw new Error(msg);
      }
      // GTM dataLayer push (no PII, no lead id)
      if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: "lead_form_submit", form_name: "contact_audit" });
      }
      setStatus("success");
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-white border-t border-[#01080E]/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-3 mb-5">
              <span className="block h-px w-8 bg-[#2AC0A5]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#01080E]">
                Contact
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold leading-[1.05] text-[#01080E]">
              Book a free
              <br />
              <span className="text-[#2AC0A5]">process audit.</span>
            </h2>
            <p className="mt-6 text-lg text-[#01080E]/75 leading-relaxed">
              Speak to our Pune-based customer operations team. Tell us what&apos;s on your plate.
              We&apos;ll map your customer operations, surface the gaps and define the outcomes
              we can own. No obligation.
            </p>

            <div className="mt-10 space-y-px bg-[#01080E]/10 border border-[#01080E]/10">
              <a
                href={`mailto:${BRAND.contact.email}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 bg-white p-5 hover:bg-[#FAFAF8] transition-colors"
              >
                <span className="inline-flex items-center justify-center w-10 h-10 bg-[#2AC0A5]/15">
                  <Mail size={18} className="text-[#01080E]" />
                </span>
                <div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#01080E]/55">
                    Email
                  </div>
                  <div className="font-medium text-[#01080E]">{BRAND.contact.email}</div>
                </div>
              </a>
              <a
                href={`tel:${BRAND.contact.phone.replace(/\s+/g, "")}`}
                data-testid="contact-phone-link"
                className="flex items-center gap-4 bg-white p-5 hover:bg-[#FAFAF8] transition-colors"
              >
                <span className="inline-flex items-center justify-center w-10 h-10 bg-[#2AC0A5]/15">
                  <Phone size={18} className="text-[#01080E]" />
                </span>
                <div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#01080E]/55">
                    Phone
                  </div>
                  <div className="font-medium text-[#01080E]">{BRAND.contact.phone}</div>
                  <a
                    href={`tel:${BRAND.contact.phoneDirect.replace(/\s+/g, "")}`}
                    data-testid="contact-phone-direct-link"
                    onClick={(e) => e.stopPropagation()}
                    className="mt-0.5 inline-block text-xs text-[#01080E]/55 hover:text-[#2AC0A5] transition-colors"
                  >
                    Direct: {BRAND.contact.phoneDirect}
                  </a>
                </div>
              </a>
              <div className="flex items-center gap-4 bg-white p-5">
                <span className="inline-flex items-center justify-center w-10 h-10 bg-[#2AC0A5]/15">
                  <MapPin size={18} className="text-[#01080E]" />
                </span>
                <div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#01080E]/55">
                    Location
                  </div>
                  <div className="font-medium text-[#01080E]">{BRAND.city}</div>
                </div>
              </div>
              <a
                href={BRAND.contact.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-linkedin-link"
                className="flex items-center gap-4 bg-white p-5 hover:bg-[#FAFAF8] transition-colors"
              >
                <span className="inline-flex items-center justify-center w-10 h-10 bg-[#2AC0A5]/15">
                  <Linkedin size={18} className="text-[#01080E]" />
                </span>
                <div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#01080E]/55">
                    LinkedIn
                  </div>
                  <div className="font-medium text-[#01080E]">Connect with us</div>
                </div>
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="bg-[#01080E] text-white p-8 md:p-10 relative overflow-hidden"
            >
              <div aria-hidden className="absolute inset-0 grid-texture-dark opacity-30 pointer-events-none" />

              <div className="relative">
                <div className="flex items-center justify-between mb-8">
                  <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#7BDCB5]">
                    / Process audit · Intake
                  </span>
                  <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50">
                    Secure · Direct to team
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field
                    label="Name"
                    testId="contact-form-name"
                    required
                    value={form.name}
                    onChange={update("name")}
                    error={fieldErrors.name}
                  />
                  <Field
                    label="Company"
                    testId="contact-form-company"
                    required
                    value={form.company}
                    onChange={update("company")}
                    error={fieldErrors.company}
                  />
                  <Field
                    label="Work Email"
                    type="email"
                    testId="contact-form-email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    error={fieldErrors.email}
                  />
                  <Field
                    label="Phone"
                    type="tel"
                    testId="contact-form-phone"
                    required
                    value={form.phone}
                    onChange={update("phone")}
                    error={fieldErrors.phone}
                  />
                </div>
                <div className="mt-5">
                  <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/55">
                    Message <span className="text-[#2AC0A5]">*</span>
                  </label>
                  <textarea
                    data-testid="contact-form-message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us about the operation you'd like governed..."
                    className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-[#2AC0A5] outline-none text-white placeholder:text-white/30 py-3 resize-none transition-colors"
                  />
                  {fieldErrors.message && (
                    <p data-testid="contact-form-message-error" className="mt-2 text-xs text-red-300">
                      {fieldErrors.message}
                    </p>
                  )}
                </div>

                {/* Honeypot — hidden from users, invisible to layout, real humans never fill this */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
                  <label>
                    Website (leave empty)
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={update("website")}
                    />
                  </label>
                </div>

                <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                  <p className="text-xs text-white/50 max-w-sm">
                    We respond to every enquiry within one business day. Your email will only be used to reply to you.
                  </p>
                  <button
                    type="submit"
                    data-testid="contact-form-submit"
                    disabled={status === "sending" || status === "success"}
                    className="group inline-flex items-center gap-2 bg-[#2AC0A5] text-[#01080E] font-semibold px-6 py-3.5 hover:bg-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "sending" ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send enquiry
                        <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>
                </div>

                {status === "success" && (
                  <div
                    data-testid="contact-form-success"
                    className="mt-6 flex items-start gap-3 border border-[#2AC0A5]/40 bg-[#2AC0A5]/10 p-4"
                  >
                    <Check size={18} className="text-[#2AC0A5] mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-white/90">
                      Thank you. Your enquiry has been received. Our team will get back to you shortly.
                    </p>
                  </div>
                )}

                {status === "error" && (
                  <div
                    data-testid="contact-form-error"
                    className="mt-6 flex items-start gap-3 border border-red-400/40 bg-red-400/10 p-4"
                  >
                    <AlertCircle size={18} className="text-red-300 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-white/90">
                      {errorMsg} If this keeps happening, write to{" "}
                      <a href={`mailto:${BRAND.contact.email}`} className="underline text-[#2AC0A5]">
                        {BRAND.contact.email}
                      </a>
                      .
                    </p>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, type = "text", value, onChange, required, testId, error }) {
  return (
    <div>
      <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/55">
        {label}
        {required && <span className="text-[#2AC0A5]"> *</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={testId}
        aria-invalid={!!error}
        className={`mt-2 w-full bg-transparent border-b outline-none text-white py-3 transition-colors ${
          error ? "border-red-400/60 focus:border-red-400" : "border-white/20 focus:border-[#2AC0A5]"
        }`}
      />
      {error && (
        <p data-testid={`${testId}-error`} className="mt-2 text-xs text-red-300">
          {error}
        </p>
      )}
    </div>
  );
}
